/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { ChartSeries } from "gui/chart/series";
import { QueryResultColumn } from "lib/results/types";
import { VisualChart } from "lib/visuals/types";
import { chartOptionPercentages } from "gui/chart/options";
import { formatDate } from "lib/dates/dates";
import { formatDateYMDHM } from "lib/dates/dates";
import { formatNumber } from "lib/numbers/numbers";
import { isNonNullable } from "lib/types/types";

// Format a chart series title.
export const formatTitle = (
  column: QueryResultColumn,
  value: unknown
): string => {
  if (!isNonNullable(value)) {
    return "";
  } else if (value instanceof Date) {
    return formatDateYMDHM(value.toISOString());
  } else if (typeof value === "string" && column.type === "datetime") {
    return formatDateYMDHM(value);
  } else {
    return truncateMiddle(String(value), 100);
  }
};

// Format a chart series value.
export const formatValue = (
  visual: VisualChart,
  _series: ChartSeries, // Unused
  column: QueryResultColumn,
  value: unknown,
  { isRightYAxisSeries = false }: { isRightYAxisSeries?: boolean } = {}
): string => {
  if (!isNonNullable(value)) {
    return "";
  } else if (value instanceof Date) {
    return formatDateYMDHM(value.toISOString());
  } else if (typeof value === "string" && column.type === "datetime") {
    return formatDateYMDHM(value);
  } else if (typeof value === "number" && chartOptionPercentages(visual)) {
    return formatNumber(value, "0.0%");
  } else if (typeof value === "number") {
    if (isRightYAxisSeries) {
      return formatNumber(
        value,
        visual.options.numberFormatRightYAxisSeries ||
          visual.options.numberFormat
      );
    } else {
      return formatNumber(value, visual.options.numberFormat);
    }
  } else {
    return String(value);
  }
};

// Format a chart axis tick value.
export const formatTick = (
  visual: VisualChart,
  series: ChartSeries,
  column: QueryResultColumn,
  value: unknown
): string => {
  if (!isNonNullable(value)) {
    return "";
  } else if (value instanceof Date) {
    return formatDateDynamically(series, column, value.toISOString());
  } else if (typeof value === "string" && column.type === "datetime") {
    return formatDateDynamically(series, column, value);
  } else if (typeof value === "number" && chartOptionPercentages(visual)) {
    return formatNumber(value, defaultTickFormats.percentage);
  } else if (typeof value === "number") {
    return formatNumber(value, defaultTickFormats.number);
  } else {
    return truncateMiddle(String(value));
  }
};

export const defaultTickFormats = {
  percentage: "0%",
  number: "0[.]0a",
};

// Format a date value dynamically based on the duration
// between the first and last date in its series.
const formatDateDynamically = (
  series: ChartSeries,
  column: QueryResultColumn,
  value: string
) => {
  const t0 = series.rows[0][column.name];
  const t1 = series.rows[series.rows.length - 1][column.name];

  if (typeof t0 !== "string" || typeof t1 !== "string") {
    return value;
  }

  return formatDate(value, t0, t1);
};

// Truncate a string by removing chars from the middle.
export const truncateMiddle = (value: string, limit = 10) => {
  if (value.length < limit) {
    return value;
  }

  const separator = "..";
  const visible = limit - separator.length;

  return [
    value.slice(0, Math.ceil(visible / 2)),
    value.slice(value.length - Math.floor(visible / 2)),
  ].join(separator);
};
